import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
} from '@chakra-ui/react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { modal, button } from '../../themes/styles'

interface FormModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  onSubmit: () => void
  isLoading?: boolean
  /** Disables the primary (submit) button. */
  isSubmitDisabled?: boolean
  size?: string
  /** Optional hard width cap on the content (e.g. "600px"); overrides `size` width. */
  maxW?: string
  children: React.ReactNode
  submitLabel?: string
  cancelLabel?: string
  closeOnOverlayClick?: boolean
  /** Chakra scroll behavior. 'inside' keeps header/footer fixed and scrolls the body (good for long forms). */
  scrollBehavior?: 'inside' | 'outside'
  /** Replace the default Cancel/Submit buttons with custom footer content. */
  footer?: React.ReactNode
  /** Hide the footer entirely. */
  hideFooter?: boolean
}

export default function FormModal({
  isOpen,
  onClose,
  title,
  onSubmit,
  isLoading = false,
  isSubmitDisabled = false,
  size = 'lg',
  maxW,
  children,
  submitLabel,
  cancelLabel,
  closeOnOverlayClick = false,
  scrollBehavior,
  footer,
  hideFooter = false,
}: FormModalProps) {
  const { t } = useTranslation()

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size={size}
      closeOnOverlayClick={closeOnOverlayClick}
      scrollBehavior={scrollBehavior}
    >
      <ModalOverlay />
      <ModalContent maxW={maxW}>
        <ModalHeader {...modal.header}>{title}</ModalHeader>
        <ModalCloseButton sx={modal.closeButton} />
        <ModalBody>
          <VStack spacing={4}>{children}</VStack>
        </ModalBody>
        {!hideFooter && (
          <ModalFooter {...modal.footer}>
            {footer ?? (
              <>
                <Button {...button.secondary} onClick={onClose}>
                  {cancelLabel ?? t('common.actions.cancel')}
                </Button>
                <Button {...button.primary} onClick={onSubmit} isLoading={isLoading} isDisabled={isSubmitDisabled}>
                  {submitLabel ?? t('common.actions.submit')}
                </Button>
              </>
            )}
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  )
}
